import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath, soccerUrl } from '../utils/paths';
import { formatGoalAssist } from '../utils/tournamentGoalUtils';
import { compressImageForUpload, isImageFile } from '../utils/imageUpload';
import { mediaUrl } from '../utils/blogUtils';
import PhotoGallery from './PhotoGallery';
import './ClubTournament.css';

const AWARD_TYPES = [
  { type: 'MVP', label: 'MVP' },
  { type: 'BEST_GOALKEEPER', label: 'Best Goalkeeper' },
  { type: 'BEST_FORWARD', label: 'Best Forward' },
  { type: 'BEST_DEFENDER', label: 'Best Defender' },
];

/** Parse backend LocalDateTime (no timezone) as wall-clock local time. */
const parseLocalDateTime = (dateStr) => {
  if (!dateStr) return null;
  const raw = String(dateStr);
  if (!raw.includes('Z') && !/[+-]\d{2}:\d{2}$/.test(raw)) {
    const [datePart, timePart = '00:00:00'] = raw.split('T');
    if (datePart) {
      const [year, month, day] = datePart.split('-').map(Number);
      const [hours, minutes, seconds] = timePart.split(':').map(Number);
      return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0, 0);
    }
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '';
  const d = parseLocalDateTime(dateStr);
  if (!d) return '';
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toDatetimeLocal = (dateStr) => {
  if (!dateStr) return '';
  const d = parseLocalDateTime(dateStr);
  if (!d) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ClubTournamentEditor = () => {
  const { id: clubId, tournamentId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [showFixtureForm, setShowFixtureForm] = useState(false);
  const [editingFixtureId, setEditingFixtureId] = useState(null);
  const [expandedFixtureId, setExpandedFixtureId] = useState(null);
  const [fixtureForm, setFixtureForm] = useState({
    homeTeam: '',
    awayTeam: '',
    round: '',
    matchDate: '',
    location: '',
    status: 'SCHEDULED',
    scoreHome: 0,
    scoreAway: 0,
  });
  const [goalForm, setGoalForm] = useState({
    minute: '',
    side: 'HOME',
    scorerPlayerId: '',
    assistPlayerId: '',
  });
  const [metaForm, setMetaForm] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    status: 'DRAFT',
  });
  const [teamPhotoFiles, setTeamPhotoFiles] = useState([]);
  const [memberPhotoPlayerId, setMemberPhotoPlayerId] = useState('');
  const [memberPhotoFile, setMemberPhotoFile] = useState(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [awardForms, setAwardForms] = useState({});

  const loadTournament = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      const [data, playersData] = await Promise.all([
        api.getClubTournament(clubId, tournamentId, user.id),
        api.getClubPlayers(clubId, user.id),
      ]);
      setTournament(data);
      setPlayers(Array.isArray(playersData) ? playersData : []);
      setMetaForm({
        name: data.name || '',
        description: data.description || '',
        startDate: data.startDate || '',
        endDate: data.endDate || '',
        status: data.status || 'DRAFT',
      });
      setError('');
    } catch (err) {
      setError(
        api.isTournamentApiUnavailable(err)
          ? 'Tournament service is not available yet. The backend may still be deploying — please try again in a few minutes.'
          : (err.message || 'Failed to load tournament')
      );
    } finally {
      setLoading(false);
    }
  }, [clubId, tournamentId, user?.id]);

  useEffect(() => {
    loadTournament();
  }, [loadTournament]);

  const topScorers = useMemo(() => {
    return [...(tournament?.playerStats || [])]
      .filter((p) => p.goals > 0 || p.assists > 0)
      .sort((a, b) => b.goals - a.goals || b.assists - a.assists);
  }, [tournament]);

  const awardsByType = useMemo(() => {
    const map = {};
    (tournament?.awards || []).forEach((a) => {
      map[a.type] = a;
    });
    return map;
  }, [tournament]);

  const flashSuccess = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 2500);
  };

  const handleSaveMeta = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setError('');
    try {
      const data = await api.updateClubTournament(clubId, tournamentId, user.id, {
        name: metaForm.name.trim(),
        description: metaForm.description,
        startDate: metaForm.startDate || null,
        endDate: metaForm.endDate || null,
        status: metaForm.status,
      });
      setTournament(data);
      flashSuccess('Tournament updated');
    } catch (err) {
      setError(err.message || 'Failed to update tournament');
    } finally {
      setActionLoading(false);
    }
  };

  const resetFixtureForm = () => {
    setFixtureForm({
      homeTeam: '',
      awayTeam: '',
      round: '',
      matchDate: '',
      location: '',
      status: 'SCHEDULED',
      scoreHome: 0,
      scoreAway: 0,
    });
    setEditingFixtureId(null);
    setShowFixtureForm(false);
  };

  const handleSaveFixture = async (e) => {
    e.preventDefault();
    if (!fixtureForm.homeTeam.trim() || !fixtureForm.awayTeam.trim()) {
      setError('Home and away teams are required');
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      const payload = {
        homeTeam: fixtureForm.homeTeam.trim(),
        awayTeam: fixtureForm.awayTeam.trim(),
        round: fixtureForm.round || null,
        // Send as local datetime (no timezone) so the backend stores the time the user picked.
        matchDate: fixtureForm.matchDate
          ? (fixtureForm.matchDate.length === 16
              ? `${fixtureForm.matchDate}:00`
              : fixtureForm.matchDate)
          : null,
        location: fixtureForm.location || null,
        status: fixtureForm.status,
        scoreHome: parseInt(fixtureForm.scoreHome, 10) || 0,
        scoreAway: parseInt(fixtureForm.scoreAway, 10) || 0,
      };
      let data;
      if (editingFixtureId) {
        data = await api.updateTournamentFixture(clubId, tournamentId, editingFixtureId, user.id, payload);
      } else {
        data = await api.addTournamentFixture(clubId, tournamentId, user.id, payload);
      }
      setTournament(data);
      resetFixtureForm();
      flashSuccess(editingFixtureId ? 'Fixture updated' : 'Fixture added');
    } catch (err) {
      setError(err.message || 'Failed to save fixture');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditFixture = (fixture) => {
    setFixtureForm({
      homeTeam: fixture.homeTeam,
      awayTeam: fixture.awayTeam,
      round: fixture.round || '',
      matchDate: fixture.matchDate ? toDatetimeLocal(fixture.matchDate) : '',
      location: fixture.location || '',
      status: fixture.status || 'SCHEDULED',
      scoreHome: fixture.scoreHome ?? 0,
      scoreAway: fixture.scoreAway ?? 0,
    });
    setEditingFixtureId(fixture.id);
    setShowFixtureForm(true);
  };

  const handleDeleteFixture = async (fixtureId) => {
    if (!window.confirm('Delete this fixture?')) return;
    setActionLoading(true);
    try {
      const data = await api.deleteTournamentFixture(clubId, tournamentId, fixtureId, user.id);
      setTournament(data);
    } catch (err) {
      setError(err.message || 'Failed to delete fixture');
    } finally {
      setActionLoading(false);
    }
  };

  const handleQuickScore = async (fixture, side, delta) => {
    setActionLoading(true);
    setError('');
    try {
      const scoreHome = side === 'HOME'
        ? Math.max(0, (fixture.scoreHome ?? 0) + delta)
        : (fixture.scoreHome ?? 0);
      const scoreAway = side === 'AWAY'
        ? Math.max(0, (fixture.scoreAway ?? 0) + delta)
        : (fixture.scoreAway ?? 0);
      const status = fixture.status === 'SCHEDULED' && delta > 0 ? 'LIVE' : fixture.status;
      const data = await api.updateTournamentFixture(clubId, tournamentId, fixture.id, user.id, {
        homeTeam: fixture.homeTeam,
        awayTeam: fixture.awayTeam,
        round: fixture.round,
        matchDate: fixture.matchDate,
        location: fixture.location,
        status,
        scoreHome,
        scoreAway,
      });
      setTournament(data);
    } catch (err) {
      setError(err.message || 'Failed to update score');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddGoal = async (fixtureId, e) => {
    e.preventDefault();
    if (!goalForm.scorerPlayerId || goalForm.minute === '') {
      setError('Minute and scorer are required');
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      const data = await api.addTournamentGoal(clubId, tournamentId, fixtureId, user.id, {
        minute: parseInt(goalForm.minute, 10),
        side: goalForm.side,
        scorerPlayerId: goalForm.scorerPlayerId,
        assistPlayerId: goalForm.assistPlayerId || null,
      });
      setTournament(data);
      setGoalForm({ minute: '', side: 'HOME', scorerPlayerId: '', assistPlayerId: '' });
      flashSuccess('Goal recorded');
    } catch (err) {
      setError(err.message || 'Failed to add goal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveGoal = async (fixtureId, goalId) => {
    if (!window.confirm('Remove this goal?')) return;
    setActionLoading(true);
    try {
      const data = await api.removeTournamentGoal(clubId, tournamentId, fixtureId, goalId, user.id);
      setTournament(data);
    } catch (err) {
      setError(err.message || 'Failed to remove goal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndFixture = async (fixture) => {
    if (!window.confirm(`Mark ${fixture.homeTeam} vs ${fixture.awayTeam} as final?`)) return;
    setActionLoading(true);
    setError('');
    try {
      const data = await api.endTournamentFixture(clubId, tournamentId, fixture.id, user.id);
      setTournament(data);
      setMetaForm((prev) => ({ ...prev, status: data.status || prev.status }));
      flashSuccess('Match marked final');
    } catch (err) {
      setError(err.message || 'Failed to end match');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartFixture = async (fixture) => {
    setActionLoading(true);
    setError('');
    try {
      const data = await api.startTournamentFixture(clubId, tournamentId, fixture.id, user.id);
      setTournament(data);
      flashSuccess('Match started');
    } catch (err) {
      setError(err.message || 'Failed to start match');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = async () => {
    setActionLoading(true);
    try {
      const data = await api.publishClubTournament(clubId, tournamentId, user.id);
      setTournament(data);
      flashSuccess('Tournament published — fans can view live scores');
    } catch (err) {
      setError(err.message || 'Failed to publish');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnpublish = async () => {
    if (!window.confirm('Unpublish tournament? Public link will stop working.')) return;
    setActionLoading(true);
    try {
      const data = await api.unpublishClubTournament(clubId, tournamentId, user.id);
      setTournament(data);
      flashSuccess('Tournament unpublished');
    } catch (err) {
      setError(err.message || 'Failed to unpublish');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      const share = await api.ensureTournamentShare(clubId, tournamentId, user.id);
      const url = soccerUrl(`/public/tournament/${share.shareToken}`);
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      setError(err.message || 'Failed to copy link');
    }
  };

  const handleShareWhatsApp = () => {
    if (!tournament?.shareToken) return;
    const url = api.shareTournamentViaWhatsApp({
      tournamentName: tournament.name,
      clubName: tournament.clubName,
      shareToken: tournament.shareToken,
      status: tournament.status,
      fixtures: tournament.fixtures,
    });
    window.open(url, '_blank');
  };

  const handleTeamPhotoSelect = (e) => {
    const files = Array.from(e.target.files || []).filter(isImageFile);
    setTeamPhotoFiles(files);
  };

  const handleUploadTeamPhotos = async () => {
    if (teamPhotoFiles.length === 0) return;
    setUploadingPhotos(true);
    setError('');
    try {
      const compressed = await Promise.all(teamPhotoFiles.map((f) => compressImageForUpload(f)));
      const data = await api.uploadTournamentTeamPhotos(clubId, tournamentId, compressed, user.id);
      setTournament(data);
      setTeamPhotoFiles([]);
      flashSuccess('Team photos uploaded');
    } catch (err) {
      setError(err.message || 'Failed to upload team photos');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeleteTeamPhoto = async (photoUrl) => {
    if (!window.confirm('Delete this team photo?')) return;
    setUploadingPhotos(true);
    try {
      const data = await api.deleteTournamentTeamPhoto(clubId, tournamentId, photoUrl, user.id);
      setTournament(data);
      flashSuccess('Photo deleted');
    } catch (err) {
      setError(err.message || 'Failed to delete photo');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleMemberPhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (file && isImageFile(file)) {
      setMemberPhotoFile(file);
    }
  };

  const handleUploadMemberPhoto = async () => {
    if (!memberPhotoPlayerId || !memberPhotoFile) {
      setError('Select a player and photo');
      return;
    }
    setUploadingPhotos(true);
    setError('');
    try {
      const player = players.find((p) => p.id === memberPhotoPlayerId);
      const compressed = await compressImageForUpload(memberPhotoFile);
      const data = await api.uploadTournamentMemberPhoto(
        clubId, tournamentId, memberPhotoPlayerId, player?.name, compressed, user.id
      );
      setTournament(data);
      setMemberPhotoFile(null);
      setMemberPhotoPlayerId('');
      flashSuccess('Member photo uploaded');
    } catch (err) {
      setError(err.message || 'Failed to upload member photo');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleDeleteMemberPhoto = async (playerId) => {
    if (!window.confirm('Delete this member photo?')) return;
    setUploadingPhotos(true);
    try {
      const data = await api.deleteTournamentMemberPhoto(clubId, tournamentId, playerId, user.id);
      setTournament(data);
      flashSuccess('Member photo deleted');
    } catch (err) {
      setError(err.message || 'Failed to delete member photo');
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handleSaveAward = async (awardType) => {
    const form = awardForms[awardType] || {};
    const playerId = form.playerId ?? awardsByType[awardType]?.playerId ?? '';
    if (!playerId) {
      setError('Select a player for this award');
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      const player = players.find((p) => p.id === playerId);
      const data = await api.updateTournamentAward(clubId, tournamentId, user.id, {
        type: awardType,
        playerId,
        playerName: player?.name,
      });
      setTournament(data);
      flashSuccess('Award updated');
    } catch (err) {
      setError(err.message || 'Failed to update award');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAwardPhotoSelect = async (awardType, e) => {
    const file = e.target.files?.[0];
    if (!file || !isImageFile(file)) return;
    setUploadingPhotos(true);
    setError('');
    try {
      const compressed = await compressImageForUpload(file);
      const data = await api.uploadTournamentAwardPhoto(clubId, tournamentId, awardType, compressed, user.id);
      setTournament(data);
      flashSuccess('Award photo uploaded');
    } catch (err) {
      setError(err.message || 'Failed to upload award photo');
    } finally {
      setUploadingPhotos(false);
      e.target.value = '';
    }
  };

  const handleDeleteAwardPhoto = async (awardType) => {
    if (!window.confirm('Delete this award photo?')) return;
    setUploadingPhotos(true);
    try {
      const data = await api.deleteTournamentAwardPhoto(clubId, tournamentId, awardType, user.id);
      setTournament(data);
      flashSuccess('Award photo deleted');
    } catch (err) {
      setError(err.message || 'Failed to delete award photo');
    } finally {
      setUploadingPhotos(false);
    }
  };

  if (loading) return <div className="loading">Loading tournament…</div>;
  if (!tournament) return <div className="alert alert-error">Tournament not found</div>;

  return (
    <div className="club-tournament-editor">
      <div className="club-tournament-editor__header">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}/admin/tournaments`))}
        >
          ← Back to tournaments
        </button>
        <div>
          <h1>{tournament.name}</h1>
          <p className="club-tournament-editor__club">{tournament.clubName}</p>
        </div>
        <div className="club-tournament-editor__status-badges">
          <span className={`tournament-badge tournament-badge--${(tournament.status || 'draft').toLowerCase()}`}>
            {tournament.status}
          </span>
          {tournament.publishedToPublic && (
            <span className="tournament-badge tournament-badge--published">Public</span>
          )}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="club-tournament-editor__actions-bar">
        {!tournament.publishedToPublic ? (
          <button type="button" className="btn btn-primary" onClick={handlePublish} disabled={actionLoading}>
            Publish to public
          </button>
        ) : (
          <>
            <button type="button" className="btn btn-secondary" onClick={handleCopyLink}>
              {linkCopied ? 'Link copied!' : 'Copy public link'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleShareWhatsApp}>
              Share on WhatsApp
            </button>
            <button type="button" className="btn btn-warning" onClick={handleUnpublish} disabled={actionLoading}>
              Unpublish
            </button>
          </>
        )}
      </div>

      <section className="tournament-section card">
        <h2>Tournament details</h2>
        <form onSubmit={handleSaveMeta} className="tournament-meta-form">
          <div className="form-group">
            <label htmlFor="meta-name">Name</label>
            <input
              id="meta-name"
              value={metaForm.name}
              onChange={(e) => setMetaForm({ ...metaForm, name: e.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="meta-desc">Description</label>
            <textarea
              id="meta-desc"
              value={metaForm.description}
              onChange={(e) => setMetaForm({ ...metaForm, description: e.target.value })}
              rows={2}
            />
          </div>
          <div className="tournament-create-form__dates">
            <div className="form-group">
              <label htmlFor="meta-start">Start</label>
              <input
                id="meta-start"
                type="date"
                value={metaForm.startDate}
                onChange={(e) => setMetaForm({ ...metaForm, startDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="meta-end">End</label>
              <input
                id="meta-end"
                type="date"
                value={metaForm.endDate}
                onChange={(e) => setMetaForm({ ...metaForm, endDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="meta-status">Status</label>
              <select
                id="meta-status"
                value={metaForm.status}
                onChange={(e) => setMetaForm({ ...metaForm, status: e.target.value })}
              >
                <option value="DRAFT">Draft</option>
                <option value="LIVE">Live</option>
                <option value="FINAL">Final</option>
              </select>
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={actionLoading}>
            Save details
          </button>
        </form>
      </section>

      <section className="tournament-section card">
        <div className="tournament-section__header">
          <h2>Fixtures & live scores</h2>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              resetFixtureForm();
              setShowFixtureForm(true);
            }}
          >
            Add fixture
          </button>
        </div>

        {showFixtureForm && (
          <form className="tournament-fixture-form" onSubmit={handleSaveFixture}>
            <h3>{editingFixtureId ? 'Edit fixture' : 'New fixture'}</h3>
            <div className="tournament-fixture-form__teams">
              <div className="form-group">
                <label>Home team *</label>
                <input
                  value={fixtureForm.homeTeam}
                  onChange={(e) => setFixtureForm({ ...fixtureForm, homeTeam: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Away team *</label>
                <input
                  value={fixtureForm.awayTeam}
                  onChange={(e) => setFixtureForm({ ...fixtureForm, awayTeam: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="tournament-create-form__dates">
              <div className="form-group">
                <label>Round</label>
                <input
                  value={fixtureForm.round}
                  onChange={(e) => setFixtureForm({ ...fixtureForm, round: e.target.value })}
                  placeholder="Group A, Semi-Final..."
                />
              </div>
              <div className="form-group">
                <label>Date & time</label>
                <input
                  type="datetime-local"
                  value={fixtureForm.matchDate}
                  onChange={(e) => setFixtureForm({ ...fixtureForm, matchDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input
                  value={fixtureForm.location}
                  onChange={(e) => setFixtureForm({ ...fixtureForm, location: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  value={fixtureForm.status}
                  onChange={(e) => setFixtureForm({ ...fixtureForm, status: e.target.value })}
                >
                  <option value="SCHEDULED">Scheduled</option>
                  <option value="LIVE">Live</option>
                  <option value="FINAL">Final</option>
                </select>
              </div>
            </div>
            <div className="tournament-fixture-form__teams">
              <div className="form-group">
                <label>Home score</label>
                <input
                  type="number"
                  min="0"
                  value={fixtureForm.scoreHome}
                  onChange={(e) => setFixtureForm({ ...fixtureForm, scoreHome: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Away score</label>
                <input
                  type="number"
                  min="0"
                  value={fixtureForm.scoreAway}
                  onChange={(e) => setFixtureForm({ ...fixtureForm, scoreAway: e.target.value })}
                />
              </div>
            </div>
            <div className="tournament-fixture-form__actions">
              <button type="submit" className="btn btn-primary" disabled={actionLoading}>
                {editingFixtureId ? 'Update fixture' : 'Add fixture'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={resetFixtureForm}>
                Cancel
              </button>
            </div>
          </form>
        )}

        {(tournament.fixtures || []).length === 0 && !showFixtureForm && (
          <p className="tournament-empty">No fixtures yet. Add a match to start tracking scores.</p>
        )}

        <div className="tournament-fixtures">
          {(tournament.fixtures || []).map((fixture) => (
            <div key={fixture.id} className={`tournament-fixture-card${fixture.status === 'LIVE' ? ' tournament-fixture-card--live' : ''}`}>
              <div className="tournament-fixture-card__meta">
                {fixture.round && <span className="tournament-fixture-card__round">{fixture.round}</span>}
                {fixture.status === 'LIVE' && <span className="tournament-live-dot">LIVE</span>}
                {fixture.status === 'FINAL' && <span className="tournament-fixture-card__final">FT</span>}
                {fixture.matchDate && <span>{formatDateTime(fixture.matchDate)}</span>}
                {fixture.location && <span>{fixture.location}</span>}
              </div>

              <div className="tournament-scoreboard">
                <div className="tournament-scoreboard__team">
                  <span className="tournament-scoreboard__name">{fixture.homeTeam}</span>
                  <div className="tournament-scoreboard__controls">
                    <button type="button" className="btn-score" onClick={() => handleQuickScore(fixture, 'HOME', -1)} disabled={actionLoading}>−</button>
                    <span className="tournament-scoreboard__score">{fixture.scoreHome ?? 0}</span>
                    <button type="button" className="btn-score" onClick={() => handleQuickScore(fixture, 'HOME', 1)} disabled={actionLoading}>+</button>
                  </div>
                </div>
                <span className="tournament-scoreboard__divider">–</span>
                <div className="tournament-scoreboard__team">
                  <span className="tournament-scoreboard__name">{fixture.awayTeam}</span>
                  <div className="tournament-scoreboard__controls">
                    <button type="button" className="btn-score" onClick={() => handleQuickScore(fixture, 'AWAY', -1)} disabled={actionLoading}>−</button>
                    <span className="tournament-scoreboard__score">{fixture.scoreAway ?? 0}</span>
                    <button type="button" className="btn-score" onClick={() => handleQuickScore(fixture, 'AWAY', 1)} disabled={actionLoading}>+</button>
                  </div>
                </div>
              </div>

              <div className="tournament-fixture-card__actions">
                {fixture.status === 'SCHEDULED' && (
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => handleStartFixture(fixture)}
                    disabled={actionLoading}
                  >
                    Start match
                  </button>
                )}
                {fixture.status === 'LIVE' && (
                  <button
                    type="button"
                    className="btn btn-warning btn-sm"
                    onClick={() => handleEndFixture(fixture)}
                    disabled={actionLoading}
                  >
                    End match
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => setExpandedFixtureId(expandedFixtureId === fixture.id ? null : fixture.id)}
                >
                  {expandedFixtureId === fixture.id ? 'Hide goals' : 'Record goal'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleEditFixture(fixture)}>
                  Edit
                </button>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => handleDeleteFixture(fixture.id)}>
                  Delete
                </button>
              </div>

              {expandedFixtureId === fixture.id && (
                <div className="tournament-goal-section">
                  <form onSubmit={(e) => handleAddGoal(fixture.id, e)} className="tournament-goal-form">
                    <div className="form-group">
                      <label>Minute</label>
                      <input
                        type="number"
                        min="0"
                        value={goalForm.minute}
                        onChange={(e) => setGoalForm({ ...goalForm, minute: e.target.value })}
                        placeholder="45"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Team</label>
                      <select
                        value={goalForm.side}
                        onChange={(e) => setGoalForm({ ...goalForm, side: e.target.value })}
                      >
                        <option value="HOME">{fixture.homeTeam}</option>
                        <option value="AWAY">{fixture.awayTeam}</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Scorer</label>
                      <select
                        value={goalForm.scorerPlayerId}
                        onChange={(e) => setGoalForm({ ...goalForm, scorerPlayerId: e.target.value })}
                        required
                      >
                        <option value="">Select player</option>
                        {players.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Assist (optional)</label>
                      <select
                        value={goalForm.assistPlayerId}
                        onChange={(e) => setGoalForm({ ...goalForm, assistPlayerId: e.target.value })}
                      >
                        <option value="">None</option>
                        {players.map((p) => (
                          <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                      </select>
                    </div>
                    <button type="submit" className="btn btn-primary btn-sm" disabled={actionLoading}>
                      Add goal
                    </button>
                  </form>

                  {(fixture.goals || []).length > 0 && (
                    <ul className="tournament-goals-list">
                      {[...(fixture.goals || [])]
                        .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0))
                        .map((g) => (
                          <li key={g.id}>
                            <span>{g.minute}&apos;</span>
                            <strong>{g.scorerPlayerName}</strong>
                            {formatGoalAssist(g) && <span> ({formatGoalAssist(g)})</span>}
                            <span className="tournament-goals-list__side">
                              {g.side === 'HOME' ? fixture.homeTeam : fixture.awayTeam}
                            </span>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => handleRemoveGoal(fixture.id, g.id)}
                            >
                              ×
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {topScorers.length > 0 && (
        <section className="tournament-section card">
          <h2>Top scorers</h2>
          <table className="tournament-stats-table">
            <thead>
              <tr>
                <th>Player</th>
                <th>Goals</th>
                <th>Assists</th>
              </tr>
            </thead>
            <tbody>
              {topScorers.map((p) => (
                <tr key={p.playerId}>
                  <td>{p.playerName}</td>
                  <td>{p.goals}</td>
                  <td>{p.assists}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <section className="tournament-section card">
        <h2>Team photos</h2>
        <p className="tournament-section__hint">
          Upload squad or team photos. These can be used in blog posts and the public tournament page.
        </p>
        <div className="tournament-photo-upload">
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleTeamPhotoSelect}
            className="file-input"
          />
          {teamPhotoFiles.length > 0 && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUploadTeamPhotos}
              disabled={uploadingPhotos}
            >
              {uploadingPhotos ? 'Uploading…' : `Upload ${teamPhotoFiles.length} photo(s)`}
            </button>
          )}
        </div>
        {(tournament.teamPhotoUrls || []).length > 0 && (
          <div className="tournament-photo-gallery-wrap">
            <PhotoGallery photos={(tournament.teamPhotoUrls || []).map(mediaUrl)} />
            <div className="tournament-photo-actions">
              {(tournament.teamPhotoUrls || []).map((url, index) => (
                <button
                  key={url}
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteTeamPhoto(url)}
                  disabled={uploadingPhotos}
                >
                  Delete photo {index + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="tournament-section card">
        <h2>Team member photos</h2>
        <p className="tournament-section__hint">
          Add individual player photos for this tournament.
        </p>
        <div className="tournament-member-photo-form">
          <div className="form-group">
            <label>Player</label>
            <select
              value={memberPhotoPlayerId}
              onChange={(e) => setMemberPhotoPlayerId(e.target.value)}
            >
              <option value="">Select player</option>
              {players.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Photo</label>
            <input type="file" accept="image/*" onChange={handleMemberPhotoSelect} />
          </div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleUploadMemberPhoto}
            disabled={uploadingPhotos || !memberPhotoPlayerId || !memberPhotoFile}
          >
            {uploadingPhotos ? 'Uploading…' : 'Upload member photo'}
          </button>
        </div>
        {(tournament.memberPhotos || []).length > 0 && (
          <div className="tournament-member-photos-grid">
            {(tournament.memberPhotos || []).map((member) => (
              <div key={member.playerId} className="tournament-member-photo-card">
                <img src={mediaUrl(member.photoUrl)} alt={member.playerName} />
                <p>{member.playerName}</p>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeleteMemberPhoto(member.playerId)}
                  disabled={uploadingPhotos}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="tournament-section card">
        <h2>Tournament awards</h2>
        <p className="tournament-section__hint">
          Set award winners and upload photos for MVP, best goalkeeper, forward, and defender.
        </p>
        <div className="tournament-awards-grid">
          {AWARD_TYPES.map(({ type, label }) => {
            const award = awardsByType[type] || {};
            const selectedPlayerId = awardForms[type]?.playerId ?? award.playerId ?? '';
            return (
              <div key={type} className="tournament-award-card">
                <h3>{label}</h3>
                <div className="form-group">
                  <label>Player</label>
                  <select
                    value={selectedPlayerId}
                    onChange={(e) => setAwardForms({
                      ...awardForms,
                      [type]: { ...awardForms[type], playerId: e.target.value },
                    })}
                  >
                    <option value="">Select player</option>
                    {players.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleSaveAward(type)}
                  disabled={actionLoading || !selectedPlayerId}
                >
                  Save player
                </button>
                {award.photoUrl ? (
                  <div className="tournament-award-photo">
                    <img src={mediaUrl(award.photoUrl)} alt={award.playerName || label} />
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteAwardPhoto(type)}
                      disabled={uploadingPhotos}
                    >
                      Remove photo
                    </button>
                  </div>
                ) : (
                  <div className="form-group">
                    <label>Photo</label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleAwardPhotoSelect(type, e)}
                      disabled={uploadingPhotos}
                    />
                  </div>
                )}
                {award.playerName && (
                  <p className="tournament-award-winner">{award.playerName}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default ClubTournamentEditor;
